import { logger } from "../services/logger.ts";
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Wallet, User, Settings, ChevronDown, Bell, Menu, X, PlusCircle, HelpCircle, TrendingUp, Crown, Gift, Code, Trophy } from 'lucide-react';
import { useAuth } from '../lib/auth.tsx';
import { useWallet } from '../lib/walletContext.tsx';
import { supabase } from '../lib/db.ts';
import { useSettings } from '../lib/settings/settingsContext.tsx';
import { UserNotificationDropdown } from './UserNotificationDropdown.tsx';
import { ShareReferralModal } from './ShareReferralModal.tsx';
import { assets, BRAND_COLOR } from '../config/brand.ts';

// Define interface for admin notifications
interface Notification {
  id: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
  link?: string;
  [key: string]: unknown;
}

function WayjaBubbleLogo() {
  return (
    <div className="relative">
      <img 
        src={assets.logos.linear}
        alt="Wayja Logo" 
        className="h-8" 
      />
    </div>
  );
}

export function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { balance } = useWallet();
  const { formatCurrency } = useSettings();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const profileRef = useRef<HTMLLIElement>(null);
  const notificationsRef = useRef<HTMLLIElement>(null);
  const helpRef = useRef<HTMLLIElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const isAdmin = user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'superuser';
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Fetch admin notifications
  useEffect(() => {
    if (isAdmin && user) {
      fetchAdminNotifications();

      // Subscribe to notification changes
      const notificationSubscription = supabase
        .channel('notification_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'admin_notifications',
            filter: `admin_id=eq.${user.id}`
          },
          () => {
            fetchAdminNotifications();
          }
        )
        .subscribe();

      return () => {
        notificationSubscription.unsubscribe();
      };
    }
  }, [isAdmin, user]);

  const fetchAdminNotifications = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('admin_notifications')
        .select('*')
        .or(`created_for.is.null,created_for.eq.${user.id}`)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      setNotifications(data || []);
      setUnreadCount((data || []).filter((n: Notification) => !n.is_read).length);
    } catch (err) {
      logger.error('Error fetching notifications:', err);
    }
  };

  const markNotificationAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('admin_notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) throw error;

      // Update the local state
      setNotifications(
        notifications.map((n: Notification) => 
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      logger.error('Error marking notification as read:', err);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    markNotificationAsRead(notification.id);
    setIsNotificationsOpen(false);
    if (notification.link) {
      navigate(notification.link);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
      if (helpRef.current && !helpRef.current.contains(event.target as Node)) {
        setIsHelpOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/markets');
    } catch (error) {
      logger.error('Error signing out:', error);
    }
  };

  const isActive = (path: string) => {
    if (path === '/markets') {
      return location.pathname === path || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header className="text-gray-900 py-4 border-b border-gray-200 sticky top-0 z-50 shadow-sm bg-white">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-md hover:bg-gray-100 transition-colors"
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
            <Link to={isAdmin && isAdminRoute ? "/admin/dashboard" : "/markets"} className="flex items-center gap-2 group">
              <WayjaBubbleLogo />
            </Link>
          </div>

          {/* Mobile Wallet Display */}
          {!isAdminRoute && user && (
            <div className="lg:hidden">
              <Link
                to="/account/wallet"
                title="Available cash, ready to place new trades"
                className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-100 hover:bg-gray-200 transition-all"
              >
                <Wallet className="w-4 h-4" />
                <span className="font-medium">{formatCurrency(balance)}</span>
              </Link>
            </div>
          )}

          {/* Mobile Login — guests only */}
          {!isAdminRoute && !user && (
            <div className="lg:hidden">
              <Link
                to="/login"
                className="px-3 py-1.5 rounded-md text-white text-sm font-medium transition-colors hover:opacity-90"
                style={{ backgroundColor: BRAND_COLOR }}
              >
                Login
              </Link>
            </div>
          )}

          {/* Desktop Navigation */}
          <nav className="hidden lg:block">
            <ul className="flex gap-8 items-center">
              {isAdminRoute && isAdmin ? (
                <>
                  <li>
                    <Link 
                      to="/admin/dashboard" 
                      className={`relative px-2 py-1 transition-colors ${
                        isActive('/admin/dashboard') 
                          ? 'text-wayja-teal font-medium' 
                          : 'text-gray-900 hover:text-wayja-teal'
                      }`}
                    >
                      Dashboard
                      {isActive('/admin/dashboard') && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-wayja-teal" />
                      )}
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/admin/markets" 
                      className={`relative px-2 py-1 transition-colors ${
                        isActive('/admin/markets') 
                          ? 'text-wayja-teal font-medium' 
                          : 'text-gray-900 hover:text-wayja-teal'
                      }`}
                    >
                      Markets
                      {isActive('/admin/markets') && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-wayja-teal" />
                      )}
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/admin/wallet" 
                      className={`relative px-2 py-1 transition-colors ${
                        isActive('/admin/wallet') 
                          ? 'text-wayja-teal font-medium' 
                          : 'text-gray-900 hover:text-wayja-teal'
                      }`}
                    >
                      Wallet
                      {isActive('/admin/wallet') && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-wayja-teal" />
                      )}
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/admin/withdrawals" 
                      className={`relative px-2 py-1 transition-colors ${
                        isActive('/admin/withdrawals') 
                          ? 'text-wayja-teal font-medium' 
                          : 'text-gray-900 hover:text-wayja-teal'
                      }`}
                    >
                      Withdrawals
                      {isActive('/admin/withdrawals') && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-wayja-teal" />
                      )}
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/admin/bulk-email" 
                      className={`relative px-2 py-1 transition-colors ${
                        isActive('/admin/bulk-email') 
                          ? 'text-wayja-teal font-medium' 
                          : 'text-gray-900 hover:text-wayja-teal'
                      }`}
                    >
                      Bulk Email
                      {isActive('/admin/bulk-email') && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-wayja-teal" />
                      )}
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/admin/support" 
                      className={`relative px-2 py-1 transition-colors ${
                        isActive('/admin/support') 
                          ? 'text-wayja-teal font-medium' 
                          : 'text-gray-900 hover:text-wayja-teal'
                      }`}
                    >
                      Support
                      {isActive('/admin/support') && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-wayja-teal" />
                      )}
                    </Link>
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <Link 
                      to={isAdmin && isAdminRoute ? "/admin/dashboard" : "/markets"} 
                      className={`relative px-2 py-1 transition-colors ${
                        isActive('/markets') 
                          ? 'text-wayja-teal font-medium' 
                          : 'text-gray-900 hover:text-wayja-teal'
                      }`}
                    >
                      Home
                      {isActive('/markets') && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-wayja-teal" />
                      )}
                    </Link>
                  </li>
                  {/* <li>
                    <Link 
                      to="/terms" 
                      className={`relative px-2 py-1 transition-colors ${
                        isActive('/terms') 
                          ? 'text-wayja-teal font-medium' 
                          : 'text-gray-900 hover:text-wayja-teal'
                      }`}
                    >
                      Terms
                      {isActive('/terms') && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-wayja-teal" />
                      )}
                    </Link>
                  </li> */}
                  
                </>
              )}
              
              {!isAdminRoute && user && (
                <>
                  {/* Primary Navigation - Essential Functions */}
                  <li>
                    <Link
                      to="/account/portfolio"
                      className={`relative px-3 py-2 transition-colors ${
                        isActive('/account/portfolio')
                          ? 'text-wayja-teal font-medium'
                          : 'text-gray-900 hover:text-wayja-teal'
                      }`}
                    >
                      Portfolio
                      {isActive('/account/portfolio') && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-wayja-teal" />
                      )}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/account/wallet"
                      title="Available cash, ready to place new trades"
                      className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                        isActive('/account/wallet')
                          ? 'bg-wayja-teal/10 text-wayja-teal border border-wayja-teal/20'
                          : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      <Wallet className="w-4 h-4" />
                      <span className="font-medium">{formatCurrency(balance)}</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/account/create-market-form"
                      className="flex items-center gap-2 px-4 py-2 rounded-md bg-wayja-teal hover:bg-opacity-90 transition-all"
                    >
                      <PlusCircle className="w-4 h-4" />
                      Create Market & Earn
                    </Link>
                  </li>
                  
                  
                  {/* Share/Referral button */}
                  <li>
                    <button
                      type="button"
                      onClick={() => setIsShareModalOpen(true)}
                      className="flex items-center gap-2 px-3 py-2 rounded-md bg-wayja-coral text-white hover:bg-opacity-90 transition-all"
                      title="Invite Friends & Earn"
                    >
                      <Gift className="w-4 h-4" />
                      <span className="hidden lg:inline">Invite Friends & Earn</span>
                    </button>
                  </li>
                </>
              )}
              
              {/* User notifications for regular users */}
              {user && !isAdminRoute && (
                <li className="relative">
                  <UserNotificationDropdown />
                </li>
              )}
              
              {/* More Dropdown - Available to all users */}
              {!isAdminRoute && (
                <li className="relative" ref={helpRef}>
                  <button
                    onClick={() => setIsHelpOpen(!isHelpOpen)}
                    className={`flex items-center gap-2 px-3 py-2 transition-colors ${
                      isHelpOpen ? 'text-wayja-teal' : 'text-gray-900 hover:text-wayja-teal'
                    }`}
                  >
                    More
                    <ChevronDown className={`w-4 h-4 transition-transform ${isHelpOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isHelpOpen && (
                    <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                      <div className="py-1" role="menu">
                        <Link
                          to="/developers"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsHelpOpen(false)}
                        >
                          <div className="flex items-center gap-2">
                            <Code className="w-4 h-4" />
                            Developer Portal
                          </div>
                        </Link>
                        <div className="border-t border-gray-200 my-1"></div>
                        <Link
                          to="/faqs"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsHelpOpen(false)}
                        >
                          FAQs
                        </Link>
                        <Link
                          to="/contact-support"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsHelpOpen(false)}
                        >
                          Contact Support
                        </Link>
                      </div>
                    </div>
                  )}
                </li>
              )}
              
              {/* Admin notifications - only show on admin routes */}
              {isAdmin && isAdminRoute && (
                <li className="relative" ref={notificationsRef}>
                  <button
                    type="button"
                    onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                    className={`relative flex items-center justify-center p-2 rounded-full transition-colors ${
                      isNotificationsOpen ? 'bg-gray-100 text-wayja-teal' : 'hover:bg-gray-100'
                    }`}
                    aria-expanded={isNotificationsOpen}
                    aria-haspopup="true"
                    aria-label={isNotificationsOpen ? 'Close admin notifications' : 'Open admin notifications'}
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-wayja-teal text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                  {isNotificationsOpen && (
                    <div className="absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                      <div className="py-1" role="menu">
                        <div className="px-4 py-2 font-medium text-gray-700 border-b">
                          Notifications
                        </div>
                        {notifications.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500">
                            No notifications
                          </div>
                        ) : (
                          <div className="max-h-96 overflow-y-auto">
                            {notifications.map(notification => (
                              <button
                                key={notification.id}
                                className={`w-full text-left px-4 py-3 text-sm border-b hover:bg-gray-50 flex flex-col gap-1 ${!notification.is_read ? 'bg-blue-50' : ''}`}
                                onClick={() => handleNotificationClick(notification)}
                              >
                                <span className="font-medium text-gray-800">{notification.title}</span>
                                <span className="text-gray-600">{notification.message}</span>
                                <span className="text-xs text-gray-400">
                                  {new Date(notification.created_at).toLocaleString()}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </li>
              )}
              {user ? (
                <li className="relative" ref={profileRef}>
                  <button
                    type="button"
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className={`flex items-center gap-2 p-2 rounded-full transition-colors ${
                      isProfileOpen ? 'bg-gray-100' : 'hover:bg-gray-100'
                    }`}
                    aria-expanded={isProfileOpen}
                    aria-haspopup="true"
                    aria-label="Account menu"
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                      {user?.user_metadata?.avatar_url ? (
                        <img
                          src={user.user_metadata.avatar_url}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-4 h-4 text-gray-600" />
                      )}
                    </div>
                    <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isProfileOpen && (
                    <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                      <div className="py-1" role="menu">
                        <Link
                          to="/account/settings"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          role="menuitem"
                          onClick={() => setIsProfileOpen(false)}
                        >
                          <div className="flex items-center gap-2">
                            <Settings className="w-4 h-4" />
                            Account Settings
                          </div>
                        </Link>
                        <Link
                          to="/account/affiliate"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          role="menuitem"
                          onClick={() => setIsProfileOpen(false)}
                        >
                          <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4" />
                            Affiliate Program
                          </div>
                        </Link>
                        <Link
                          to="/account/creator"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          role="menuitem"
                          onClick={() => setIsProfileOpen(false)}
                        >
                          <div className="flex items-center gap-2">
                            <Crown className="w-4 h-4" />
                            Creator Dashboard
                          </div>
                        </Link>
                        <div className="border-t border-gray-200 my-1"></div>
                        <button
                          onClick={() => { handleLogout(); setIsProfileOpen(false); }}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                          style={{ color: '#5cb3ad' }}
                          role="menuitem"
                        >
                          <div className="flex items-center gap-2">
                            <LogOut className="w-4 h-4" />
                            Sign Out
                          </div>
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ) : (
                <li>
                  <Link
                    to="/login"
                    className="px-4 py-2 rounded-md text-white font-medium transition-colors hover:opacity-90"
                    style={{ backgroundColor: BRAND_COLOR }}
                  >
                    Login
                  </Link>
                </li>
              )}
            </ul>
          </nav>
        </div>

        {/* Mobile Navigation */}
        <div
          ref={mobileMenuRef}
          className={`fixed inset-0 z-50 lg:hidden transform transition-transform duration-300 ease-in-out ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="absolute inset-0 bg-black bg-opacity-70" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-64 bg-white border-l border-gray-200 shadow-xl">
            <div className="h-full flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-xl font-bold text-gray-900">Menu</span>
                  <button
                    type="button"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 rounded-md hover:bg-gray-100 transition-colors"
                    aria-label="Close menu"
                  >
                    <X className="w-6 h-6 text-gray-600" />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <ul className="space-y-4">
                  {isAdminRoute && isAdmin ? (
                    <>
                      <li>
                        <Link
                          to="/admin/dashboard"
                          className={`block px-4 py-2 rounded-md transition-colors ${
                            isActive('/admin/dashboard')
                              ? 'bg-wayja-teal/10 text-wayja-teal'
                              : 'hover:bg-gray-100 text-gray-700'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Dashboard
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/admin/markets"
                          className={`block px-4 py-2 rounded-md transition-colors ${
                            isActive('/admin/markets')
                              ? 'bg-wayja-teal/10 text-wayja-teal'
                              : 'hover:bg-gray-100 text-gray-700'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Markets
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/admin/wallet"
                          className={`block px-4 py-2 rounded-md transition-colors ${
                            isActive('/admin/wallet')
                              ? 'bg-wayja-teal/10 text-wayja-teal'
                              : 'hover:bg-gray-100 text-gray-700'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Wallet
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/admin/withdrawals"
                          className={`block px-4 py-2 rounded-md transition-colors ${
                            isActive('/admin/withdrawals')
                              ? 'bg-wayja-teal/10 text-wayja-teal'
                              : 'hover:bg-gray-100 text-gray-700'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Withdrawals
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/admin/bulk-email"
                          className={`block px-4 py-2 rounded-md transition-colors ${
                            isActive('/admin/bulk-email')
                              ? 'bg-wayja-teal/10 text-wayja-teal'
                              : 'hover:bg-gray-100 text-gray-700'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Bulk Email
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/admin/notifications"
                          className={`block px-4 py-2 rounded-md transition-colors ${
                            isActive('/admin/notifications')
                              ? 'bg-wayja-teal/10 text-wayja-teal'
                              : 'hover:bg-gray-100 text-gray-700'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Notifications
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/admin/support"
                          className={`block px-4 py-2 rounded-md transition-colors ${
                            isActive('/admin/support')
                              ? 'bg-wayja-teal/10 text-wayja-teal'
                              : 'hover:bg-gray-100 text-gray-700'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Support
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/markets"
                          className="block px-4 py-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Switch to User View
                        </Link>
                      </li>
                    </>
                  ) : (
                    <>
                      {/* Public Navigation */}
                      <li>
                        <Link
                          to={isAdmin && isAdminRoute ? "/admin/dashboard" : "/markets"}
                          className={`block px-4 py-2 rounded-md transition-colors ${
                            isActive('/markets')
                              ? 'bg-wayja-teal/10 text-wayja-teal'
                              : 'hover:bg-gray-100 text-gray-700'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Home
                        </Link>
                      </li>
                      {/* <li>
                        <Link
                          to="/about"
                          className={`block px-4 py-2 rounded-md transition-colors ${
                            isActive('/about')
                              ? 'bg-wayja-teal/10 text-wayja-teal'
                              : 'hover:bg-gray-100 text-gray-700'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          About Us
                        </Link>
                      </li> */}
                    </>
                  )}
                  
                  {isAdmin && !isAdminRoute && (
                    <li className="mt-4 pt-4 border-t border-gray-200">
                      <Link
                        to="/admin/dashboard"
                        className="block px-4 py-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Admin Dashboard
                      </Link>
                    </li>
                  )}
                  {!isAdminRoute && user && (
                    <>
                      {/* Primary Navigation - Core App Functions */}
                      <li>
                        <Link
                          to="/account/portfolio"
                          className={`block px-4 py-2 rounded-md transition-colors ${
                            isActive('/account/portfolio')
                              ? 'bg-wayja-teal/10 text-wayja-teal'
                              : 'hover:bg-gray-100 text-gray-700'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            <span>Portfolio</span>
                          </div>
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/account/wallet"
                          title="Available cash, ready to place new trades"
                          className={`block px-4 py-2 rounded-md transition-colors ${
                            isActive('/account/wallet')
                              ? 'bg-wayja-teal/10 text-wayja-teal'
                              : 'hover:bg-gray-100 text-gray-700'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <div className="flex items-center gap-2">
                            <Wallet className="w-4 h-4" />
                            <span>Wallet ({formatCurrency(balance)})</span>
                          </div>
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/account/create-market-form"
                          className="block px-4 py-2 rounded-md bg-wayja-teal hover:bg-opacity-90 transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <div className="flex items-center gap-2">
                            <PlusCircle className="w-4 h-4" />
                            <span>Create Market</span>
                          </div>
                        </Link>
                      </li>
                      <li>
                        <button
                          type="button"
                          onClick={() => { setIsShareModalOpen(true); setIsMobileMenuOpen(false); }}
                          className="w-full text-left block px-4 py-2 rounded-md bg-wayja-coral text-white hover:bg-opacity-90 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <Gift className="w-4 h-4" />
                            <span>Invite Friends & Earn</span>
                          </div>
                        </button>
                      </li>
                      <li>
                        <Link
                          to="/leaderboard"
                          className={`block px-4 py-2 rounded-md transition-colors ${
                            location.pathname === '/leaderboard'
                              ? 'bg-wayja-teal/10 text-wayja-teal'
                              : 'hover:bg-gray-100 text-gray-700'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <div className="flex items-center gap-2">
                            <Trophy className="w-4 h-4" />
                            <span>Leaderboard</span>
                          </div>
                        </Link>
                      </li>
                    </>
                  )}
                  {user ? (
                    <>
                      {/* Support Section */}
                      <li className="px-4 py-2 text-xs text-gray-400 uppercase tracking-wider font-semibold mt-4">
                        Support & Resources
                      </li>
                      <li>
                        <Link 
                          to="/developers" 
                          className="block px-4 py-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors" 
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <div className="flex items-center gap-2">
                            <Code className="w-4 h-4" />
                            <span>Developer Portal</span>
                          </div>
                        </Link>
                      </li>
                      <li>
                        <Link 
                          to="/faqs" 
                          className="block px-4 py-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors" 
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <div className="flex items-center gap-2">
                            <HelpCircle className="w-4 h-4" />
                            <span>Help & FAQs</span>
                          </div>
                        </Link>
                      </li>
                      <li>
                        <Link 
                          to="/contact-support" 
                          className="block px-4 py-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors" 
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <div className="flex items-center gap-2">
                            <HelpCircle className="w-4 h-4" />
                            <span>Contact Support</span>
                          </div>
                        </Link>
                      </li>
                      {/* <li>
                        <Link
                          to="/terms"
                          className={`block px-4 py-2 rounded-md transition-colors ${
                            isActive('/terms')
                              ? 'bg-wayja-teal/10 text-wayja-teal'
                              : 'hover:bg-gray-100 text-gray-700'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            <span>Terms & Conditions</span>
                          </div>
                        </Link>
                      </li> */}
                      
                      {/* User Account Section */}
                      <li className="px-4 py-2 text-xs text-gray-400 uppercase tracking-wider font-semibold mt-4">
                        Account
                      </li>
                      <li>
                        <Link
                          to="/account/settings"
                          className={`block px-4 py-2 rounded-md transition-colors ${
                            isActive('/account/settings')
                              ? 'bg-wayja-teal/10 text-wayja-teal'
                              : 'hover:bg-gray-100 text-gray-700'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <div className="flex items-center gap-2">
                            <Settings className="w-4 h-4" />
                            <span>Account Settings</span>
                          </div>
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/account/affiliate"
                          className={`block px-4 py-2 rounded-md transition-colors ${
                            isActive('/account/affiliate')
                              ? 'bg-wayja-teal/10 text-wayja-teal'
                              : 'hover:bg-gray-100 text-gray-700'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4" />
                            <span>Affiliate Program</span>
                          </div>
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/account/creator"
                          className={`block px-4 py-2 rounded-md transition-colors ${
                            isActive('/account/creator')
                              ? 'bg-wayja-teal/10 text-wayja-teal'
                              : 'hover:bg-gray-100 text-gray-700'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <div className="flex items-center gap-2">
                            <Crown className="w-4 h-4" />
                            <span>Creator Dashboard</span>
                          </div>
                        </Link>
                      </li>
                      
                      {/* Sign Out */}
                      <li className="mt-4 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                          className="w-full text-left px-4 py-2 rounded-md hover:bg-gray-100 transition-colors"
                          style={{ color: '#5cb3ad' }}
                        >
                          <div className="flex items-center gap-2">
                            <LogOut className="w-4 h-4" />
                            <span>Sign Out</span>
                          </div>
                        </button>
                      </li>
                    </>
                  ) : (
                    <li>
                      <Link
                        to="/login"
                        className="block px-4 py-2 rounded-md text-white font-medium transition-colors hover:opacity-90"
                        style={{ backgroundColor: BRAND_COLOR }}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Login
                      </Link>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Share Referral Modal */}
      <ShareReferralModal 
        isOpen={isShareModalOpen} 
        onClose={() => setIsShareModalOpen(false)} 
      />
    </header>
  );
}